import { useEffect, useRef, useState, type ReactNode } from "react";
import type { StudioModuleConnector } from "../../services/studio/types";
import styles from "./ConnectorTabs.module.css";

interface ConnectorTabsProps {
  readonly connectors: readonly StudioModuleConnector[];
  readonly renderPanel: (connector: StudioModuleConnector) => ReactNode;
  readonly ariaLabel?: string;
}

/**
 * Renders one tab per linked connector when a module has 2+ connectors.
 * With a single connector, the panel renders directly without a tab strip
 * (matches the legacy single-connector module layout). Tab order follows
 * the order of the connectors[] array — which is sequenced server-side
 * by the settings endpoint.
 */
export function ConnectorTabs({
  connectors,
  renderPanel,
  ariaLabel,
}: Readonly<ConnectorTabsProps>) {
  const [activeIdx, setActiveIdx] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (activeIdx >= connectors.length) {
      setActiveIdx(0);
    }
  }, [connectors.length, activeIdx]);

  if (connectors.length === 0) return null;
  if (connectors.length === 1) {
    const only = connectors[0];
    return only ? <>{renderPanel(only)}</> : null;
  }

  const safeIdx = Math.min(activeIdx, connectors.length - 1);
  const active = connectors[safeIdx];

  function handleKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    let target: number | null = null;
    if (e.key === "ArrowRight") target = (index + 1) % connectors.length;
    else if (e.key === "ArrowLeft") target = (index - 1 + connectors.length) % connectors.length;
    else if (e.key === "Home") target = 0;
    else if (e.key === "End") target = connectors.length - 1;
    if (target === null) return;
    e.preventDefault();
    setActiveIdx(target);
    tabRefs.current[target]?.focus();
  }

  return (
    <div className={styles.wrap}>
      <div
        className={styles.strip}
        role="tablist"
        aria-label={ariaLabel ?? "Linked connections"}
      >
        {connectors.map((c, i) => {
          const isActive = i === safeIdx;
          return (
            <button
              key={c.id}
              ref={(el) => {
                tabRefs.current[i] = el;
              }}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`connector-panel-${c.id}`}
              id={`connector-tab-${c.id}`}
              tabIndex={isActive ? 0 : -1}
              className={`${styles.tab} ${isActive ? styles.tabActive : ""}`}
              onClick={() => setActiveIdx(i)}
              onKeyDown={(e) => handleKeyDown(e, i)}
            >
              {c.name}
            </button>
          );
        })}
      </div>
      {active && (
        <div
          className={styles.panel}
          role="tabpanel"
          id={`connector-panel-${active.id}`}
          aria-labelledby={`connector-tab-${active.id}`}
        >
          {renderPanel(active)}
        </div>
      )}
    </div>
  );
}
